<template>
  <Layout>
    <!-- Title Header -->
    <div class="row">
      <PageHeader class="col-8" title="Speed Limit Rule Management" />
      <div class="col-sm-4">
        <div class="text-sm-end">
          <button type="button" @click="showAddRuleModal" class="btn btn-info btn-rounded mb-2 me-2">
            <i class="mdi mdi-plus me-1"></i>{{ $t("menuitems.administration.list.speed.list.add") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Company Table -->
    <div class="table-responsive mb-0">
      <b-table class="datatables" :items="speedRulesData" :fields="fields" responsive="sm" :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc" :filter="filter" :filter-included-fields="filterOn">

        <template #cell(created_at)="row">
          <span>{{ new Date(row.item.created_at).toDateString() }}
            {{ new Date(row.item.created_at).toLocaleTimeString() }}</span>
        </template>

        <template #cell(min_speed)="row">
          <span>{{ row.item.min_speed }} km/hr</span>
        </template>

        <template #cell(max_speed)="row">
          <span>{{ row.item.max_speed }} km/hr</span>
        </template>

        <template #cell(actions)="row">
          <b-button size="sm" variant="primary" class="m-1" @click="showUpdateRuleModal(row.item)">
            Edit
          </b-button>
          <b-button size="sm" variant="danger" class="m-1" @click="deleteRule(row.item)">
            Delete
          </b-button>
        </template>
      </b-table>
    </div>

    <!-- Create speed limit modal -->
    <b-modal id="create-speed-rule-modal" title="Create New Rule" title-class="font-18" size="lg" no-close-on-backdrop
      hide-footer>
      <template #modal-header>
        <div>
          <h5>Create New Speed Rule</h5>
        </div>
        <div>
          <button class="btn btn-sm btn-outline-danger" @click="closeAddNewModal()">
            Cancel
          </button>
        </div>
      </template>
      <form @submit.prevent="createNewSpeedRule">
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table mb-0">
                    <tbody>
                      <tr>
                        <th>Vehicle Class</th>
                        <td>
                          <v-select class="style-chooser" v-model="vehicleClassId" :options="vehicleClassMaster"
                            :reduce="(v) => v.id" label="class_name"></v-select>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Min. Speed Required</th>
                        <td>
                          <input placeholder="Enter Min Speed" v-model="minSpeed" type="number" min="0" max="400"
                            class="form-control inp-bg" required />
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Max Speed Allowed</th>
                        <td>
                          <input placeholder="Enter Max Speed" v-model="maxSpeed" type="number" min="0" max="400"
                            class="form-control inp-bg" required />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="mt-2 text-center">
              <button type="submit" class="btn btn-primary me-1 w-50">
                Create Speed Rule <i class="bx bx-rocket ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>

    <!-- Edit and Update Speed Rule -->
    <b-modal id="edit-speed-rule-modal" title="Create New Company" title-class="font-18" no-close-on-backdrop hide-footer
      size="lg">
      <template #modal-header>
        <div>
          <h5>Update Speed Rule</h5>
        </div>
        <div>
          <button class="btn btn-sm btn-outline-danger" @click="$bvModal.hide('edit-speed-rule-modal')">
            Cancel
          </button>
        </div>
      </template>
      <form @submit.prevent="updateSpeedRule">
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table mb-0">
                    <tbody>
                      <tr>
                        <th>Vehicle Class</th>
                        <td>
                          <v-select class="style-chooser" v-model="selectedSpeedRule.vehicleClassId"
                            :options="vehicleClassMaster" :reduce="(v) => v.id" label="class_name"></v-select>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Min. Speed Required</th>
                        <td>
                          <input placeholder="Enter Min Speed" v-model="selectedSpeedRule.minSpeed" type="number" min="0"
                            max="400" class="form-control inp-bg" required />
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Max Speed Allowed</th>
                        <td>
                          <input placeholder="Enter Max Speed" v-model="selectedSpeedRule.maxSpeed" type="number" min="0"
                            max="400" class="form-control inp-bg" required />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="mt-2 text-center">
              <button type="submit" class="btn btn-primary me-1 w-50">
                Update Speed Rule <i class="bx bx-rocket ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>
  </Layout>
</template>
<script>
import Layout from "@/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import incidentMixin from "@/mixins/incident";
import adminMixin from "@/mixins/administration";
import Swal from "sweetalert2";

/**
 * Speed Limit Component
 */
export default {
  page: {
    title: "Speed Limit Rule Management",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },

  mixins: [incidentMixin, adminMixin],

  data () {
    return {
      // Get variable
      speedRulesData: [],

      // Masters
      packageMaster: [],
      vehicleClassMaster: [],

      // Form variables
      vehicleClassId: null,
      minSpeed: null,
      maxSpeed: null,

      // Update variable
      selectedSpeedRule: {},

      // Tables variables
      sortBy: "id",
      sortDesc: false,
      filterOn: [],
      filter: null,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: false,
        },
        {
          key: "class_name",
          label: "Vehicle Class",
          sortable: false,
        },
        {
          key: "min_speed",
          label: "Min. Speed",
          sortable: false,
        },
        {
          key: "max_speed",
          label: "Max Allowed Speed",
          sortable: false,
        },
        {
          key: "actions",
          sortable: false,
          label: "Actions",
        },
      ],
    };
  },

  components: {
    Layout,
    PageHeader,
  },

  async mounted () {
    try {
      const data = await this.GetIncidentMasters();
      this.packageMaster = data.getPackages;
      this.vehicleClassMaster = data.getVehicleClass;

      this.speedRulesData = await this.GetSpeedRules();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      Swal.fire(
        "Network Error",
        "Failed to fetch data from the server. Please check your internet connection.",
        "error"
      );
    }
  },

  methods: {
    showAddRuleModal () {
      this.$bvModal.show("create-speed-rule-modal");
    },

    showUpdateRuleModal (data) {
      this.selectedSpeedRule = {
        id: data.id,
        classId: data.class_id,
        minSpeed: data.min_speed,
        maxSpeed: data.max_speed,
      };

      this.$bvModal.show("edit-speed-rule-modal");
    },

    closeAddNewModal () {
      this.vehicleClassId = null;
      this.minSpeed = null;
      this.maxSpeed = null;
      this.$bvModal.hide("create-speed-rule-modal");
    },

    async createNewSpeedRule () {
      try {
        const filterData = this.speedRulesData.filter((classData) => classData.class_id === this.vehicleClassId)
        if (filterData.length === 0) {
          let data = {
            classId: this.vehicleClassId,
            minSpeed: this.minSpeed,
            maxSpeed: this.maxSpeed,
          };

          if (data.minSpeed > data.maxSpeed || data.minSpeed == data.maxSpeed) {
            Swal.fire(
              "Failed!",
              "Minimum speed should be less than maximum speed",
              "warning"
            );
          } else {
            this.$store.dispatch("layout/changeLoaderValue", true);
            await this.SaveSpeedRule(data);
            this.vehicleClassId = null;
            this.minSpeed = null;
            this.maxSpeed = null;

            this.speedRulesData = await this.GetSpeedRules();

            this.$store.dispatch("layout/changeLoaderValue", false);
            this.$bvModal.hide("create-speed-rule-modal");
            Swal.fire(
              "Speed Rule Created",
              "Speed Rule created successfully",
              "success"
            );
          }
        } else {
          Swal.fire("Failed!", "Duplicate record found with same vehicle class", "error");
        }
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", "Failed to create Speed Rule", "error");
      }
    },

    async updateSpeedRule () {
      try {
        let data = {
          id: this.selectedSpeedRule.id,
          classId: this.selectedSpeedRule.vehicleClassId,
          minSpeed: this.selectedSpeedRule.minSpeed,
          maxSpeed: this.selectedSpeedRule.maxSpeed,
        };

        this.$store.dispatch("layout/changeLoaderValue", true);

        await this.SaveSpeedRule(data);

        this.selectedSpeedRule = {};

        this.speedRulesData = await this.GetSpeedRules();

        this.$store.dispatch("layout/changeLoaderValue", false);
        this.$bvModal.hide("edit-speed-rule-modal");
        Swal.fire(
          "Speed Rule Updated",
          "Speed Rule updated successfully",
          "success"
        );
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", "Failed to update Speed Rule", "error");
      }
    },

    async deleteRule (data) {
      try {
        const id = {
          id: data.id,
        };

        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
        }).then(async (result) => {
          if (result.isConfirmed) {
            Swal.fire("Deleted!", "Speed rule has been deleted.", "success");
            await this.DeleteSpeedRule(id);
            this.speedRulesData = await this.GetSpeedRules();
          }
        });
      } catch (e) {
        Swal.fire("Failed!", "Failed to delete Speed Rule", "error");
      }
    },
  },
};
</script>
